if (!customElements.get('card-product')) {
  customElements.define(
    'card-product',
    class CardProduct extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          previousButton: '[data-media-previous]',
          nextButton: '[data-media-next]',
          swatchContainer: '[data-swatch-container]',
          swatchButtons: '[data-swatch-button]',
          secondaryImages: "img[data-secondary='true']",
          showMoreButton: '[data-show-more]',
        };

        this.hoverImages = this.querySelectorAll(this.selectors.secondaryImages);
        this.previousButton = this.querySelector(this.selectors.previousButton);
        this.nextButton = this.querySelector(this.selectors.nextButton);
        this.swatchContainer = this.querySelector(this.selectors.swatchContainer);
        this.swatchButtons = this.querySelectorAll(this.selectors.swatchButtons);
        this.showMoreButton = this.querySelector(this.selectors.showMoreButton);
        this.templates = this.querySelectorAll('template');

        if (this.previousButton && this.nextButton) {
          this.initMediaGalleryButtons();
          this.addEventListener('mouseleave', () => this.resetState());
        }

        if (this.swatchButtons.length) this.initSwatchButtons();

        this.addEventListener(
          'mouseover',
          () => {
            if (this.previousButton && this.nextButton) {
              this.preloadImages(this.hoverImages);
              this.templates.forEach((template) => {
                const img = template.content.cloneNode(true).querySelector('img');
                if (img) new Image().src = img.src;
              });
            }
            if (this.swatchContainer && this.showMoreButton) {
              this.initContainerWatcher();
              this.observeSwatchContainerResize();
            }
          },
          { once: true },
        );
      }

      observeSwatchContainerResize() {
        if (!this.swatchContainer || this._swatchResizeObserved) return;
        this._swatchResizeObserved = true;
        let rafId = null;
        const observer = new ResizeObserver(() => {
          if (rafId != null) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(() => {
            rafId = null;
            this.initContainerWatcher();
          });
        });
        observer.observe(this.swatchContainer);
        this._swatchResizeObserver = observer;
      }

      initContainerWatcher() {
        if (!this.swatchContainer || !this.showMoreButton) return;

        this.swatchButtons.forEach((btn) => btn.classList.remove('sm:hidden'));
        const swatchGap = parseInt(this.swatchContainer.dataset.flexGap, 10);
        const desktopMax = parseInt(this.swatchContainer.dataset.desktopSwatchMax, 10);
        const containerWidth = this.swatchContainer.getBoundingClientRect().width;
        const showMoreWidth = this.showMoreButton.getBoundingClientRect().width;
        const buttonsToMeasure = Number.isNaN(desktopMax)
          ? Array.from(this.swatchButtons)
          : Array.from(this.swatchButtons).slice(0, desktopMax);
        const availableWidth = containerWidth - swatchGap - showMoreWidth;
        let totalWidth = 0;
        let lastIndexThatFits = -1;

        for (let i = 0; i < buttonsToMeasure.length; i++) {
          const button = buttonsToMeasure[i];
          const width = button.getBoundingClientRect().width;
          if (width === 0) continue;
          const needWidth = totalWidth + width + (i > 0 ? swatchGap : 0);
          if (needWidth <= availableWidth) {
            totalWidth = needWidth;
            lastIndexThatFits = i;
          } else {
            break;
          }
        }

        buttonsToMeasure.forEach((button, i) => {
          if (i > lastIndexThatFits) button.classList.add('hidden', 'sm:hidden');
        });
        if (lastIndexThatFits < buttonsToMeasure.length - 1) {
          this.showMoreButton.classList.remove('invisible', 'sm:invisible');
        } else {
          this.showMoreButton.classList.add('invisible', 'sm:invisible');
        }
      }

      preloadImages(images) {
        images.forEach((image, index) => {
          // First image is visible and loaded
          if (index === 0) return;
          let img = new Image();
          img.src = image.src;
        });
      }

      initSwatchButtons() {
        this.swatchButtons.forEach((button) => {
          button.addEventListener('click', (e) => {
            if (e.currentTarget.dataset.active === 'true') return;

            this.swatchButtons.forEach((button) => {
              button.firstElementChild.classList.toggle('active', button === e.currentTarget);
              button.setAttribute('data-active', button === e.currentTarget);
            });

            const template = this.querySelector(`template[data-index="${e.currentTarget.dataset.targetIndex}"]`);

            this.setActiveTemplate(template);

            const templateClone = template.content.cloneNode(true);

            // The following commented out code handles showing the same image index that was previously selected.
            // The index: this.targetIndex is set when an image is rotated. We're simply showing the same image that the index was set to
            // NOTE the product image counts will need to all be the same. If a swatch shows 4 images on hover, all swatches must show 4 images
            // The featured image can be a swatch image, if this is the case the count for that swatch will be -1.
            // See comment below about pre / next button state. It can be removed if we go this path.

            // const images = templateClone.querySelectorAll("img");
            // images.forEach((img, index) => {
            //   img.classList.toggle("hidden", this.targetIndex !== index);
            // });

            template.after(templateClone);
            this.hoverImages.forEach((img) => img.remove());
            this.hoverImages = this.querySelectorAll(this.selectors.secondaryImages);

            this.preloadImages(this.hoverImages);

            // Resets prev / next button states. Can be removed if state is maintained through swatch images
            this.previousButton.classList.add('hidden');
            this.nextButton.classList.toggle('hidden', this.hoverImages.length === 1);
          });
        });
      }

      initMediaGalleryButtons() {
        this.previousButton.addEventListener('click', (e) => {
          const activeImage = this.getActiveImage();
          const activeIndex = this.getActiveIndex(activeImage);

          this.targetIndex = activeIndex === 0 ? this.hoverImages.length - 1 : activeIndex - 1;

          this.nextButton.classList.remove('hidden');

          if (this.targetIndex !== -1) {
            this.toggleImages([activeImage, this.hoverImages[this.targetIndex]]);
          }
        });

        this.nextButton.addEventListener('click', (e) => {
          const activeImage = this.getActiveImage();
          const activeIndex = this.getActiveIndex(activeImage);
          this.targetIndex = activeIndex === this.hoverImages.length - 1 ? 0 : activeIndex + 1;
          this.previousButton.classList.remove('hidden');

          if (this.targetIndex !== this.hoverImages.length) {
            this.toggleImages([activeImage, this.hoverImages[this.targetIndex]]);
          }
        });
      }

      toggleImages(els) {
        els.forEach((el) => el.classList.toggle('hidden'));
      }

      getActiveImage() {
        return this.querySelector(`${this.selectors.secondaryImages}:not(.hidden)`);
      }

      getActiveIndex(activeImage) {
        return Array.from(this.hoverImages).findIndex((image) => image === activeImage);
      }

      handleImageChange() {
        const activeImage = this.querySelector(`${this.selectors.secondaryImages}:not(.hidden)`);
        const activeIndex = Array.from(this.hoverImages).findIndex((image) => image === activeImage);
        activeImage.classList.toggle('hidden');

        this.hoverImages[activeIndex + 1].classList.toggle('hidden');
      }

      resetState() {
        // Timeout required due to fade out transition
        setTimeout(() => {
          if (this.templates.length) {
            const template = this.querySelector(`template[data-index="0"]`);
            if (template.dataset.active === 'true') {
              this.hoverImages.forEach((img, index) => {
                img.classList.toggle('hidden', index);
              });
            } else {
              this.setActiveTemplate(template);
              const templateClone = template.content.cloneNode(true);
              template.after(templateClone);
              this.hoverImages.forEach((img) => img.remove());
              this.hoverImages = this.querySelectorAll(this.selectors.secondaryImages);
            }
          } else {
            this.hoverImages.forEach((img, index) => {
              img.classList.toggle('hidden', index);
            });
          }

          this.swatchButtons.forEach((button, index) => {
            button.firstElementChild.classList.toggle('active', !index);
            button.setAttribute('data-active', !index);
          });

          this.previousButton.classList.add('hidden');
          this.nextButton.classList.toggle('hidden', this.hoverImages.length === 1);
        }, 200);
      }

      setActiveTemplate(template) {
        this.templates.forEach((el) => el.setAttribute('data-active', false));
        template.setAttribute('data-active', true);
      }

      disconnectedCallback() {
        this._swatchResizeObserver?.disconnect();
      }
    },
  );
}
